/**
 * Hooks de dados de Consulta (M7) sobre React Query.
 * Consomem a API REAL (ConsultaApi); erros são genéricos (RN-GLOBAL-004) e
 * nenhum dado pessoal é enviado a analytics/console (RN-M11-001).
 */

import { useQuery } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import {
  EscolaFicha,
  EscolaFiltros,
  EscolaListItem,
  PaginaLista,
  Pastas,
  VisitaFicha,
  VisitaFiltros,
  VisitaListItem,
} from '../services/consulta-types';

interface EstadoLista<T> {
  readonly pagina: PaginaLista<T> | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** GET /escolas — lista de escolas (com filtros). */
export function useEscolas(filtros: EscolaFiltros): EstadoLista<EscolaListItem> {
  const { consulta } = useServices();
  const query = useQuery({
    queryKey: ['escolas', filtros],
    queryFn: () => consulta.listarEscolas(filtros),
  });
  return { pagina: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /escolas/{inep} — ficha da escola. */
export function useEscola(inep: string | null): {
  ficha: EscolaFicha | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { consulta } = useServices();
  const query = useQuery({
    queryKey: ['escola', inep],
    queryFn: () => consulta.obterEscola(inep as string),
    enabled: inep !== null,
  });
  return { ficha: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /visitas — lista de visitas (com filtros combinados). */
export function useVisitas(filtros: VisitaFiltros): EstadoLista<VisitaListItem> {
  const { consulta } = useServices();
  const query = useQuery({
    queryKey: ['visitas', filtros],
    queryFn: () => consulta.listarVisitas(filtros),
  });
  return { pagina: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /visitas/{id} — ficha da visita. */
export function useVisita(id: string | null): {
  ficha: VisitaFicha | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { consulta } = useServices();
  const query = useQuery({
    queryKey: ['visita', id],
    queryFn: () => consulta.obterVisita(id as string),
    enabled: id !== null,
  });
  return { ficha: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /pastas[/{caminho}] — árvore de pastas. */
export function usePastas(caminho: string): {
  arvore: Pastas | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { consulta } = useServices();
  const query = useQuery({
    queryKey: ['pastas', caminho],
    queryFn: () => consulta.navegarPastas(caminho),
  });
  return { arvore: query.data, carregando: query.isLoading, erro: query.isError };
}
